import React, { useRef } from 'react';
import ComparisonCard from './ComparisonCard';
import ComparisonCardSkeleton from './ComparisonCardSkeleton';
import type { ComparisonCardData } from '@app-types/comparison';
import { useCompanyNameLineCount } from './useCompanyNameLineCount';

export interface ComparisonCardGridProps {
  cards: ComparisonCardData[];
  nodeId?: string;
  /** When true, render `placeholderCount` skeleton cards instead of `cards`. */
  isLoading?: boolean;
  /** Number of skeleton cards to render while loading. */
  placeholderCount?: number;
}

/**
 * Grid wrapper for the Comparison screen's 10 cards.
 * Layout classes (`.comparison-card-grid`) live in ComparisonPage.less so the
 * page owns the vertical rhythm and the desktop/mobile column counts.
 *
 * Figma parents:
 *   - Desktop: 3184:8701 (626 × 1809, 2 columns of 304px)
 *   - Mobile:  3190:14475 (343 × 3634, single column of 343px)
 */
const ComparisonCardGrid: React.FC<ComparisonCardGridProps> = ({
  cards,
  nodeId,
  isLoading = false,
  placeholderCount = 0,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const nameLines = useCompanyNameLineCount(containerRef, cards, isLoading);
  return (
    <div
      ref={containerRef}
      className="comparison-card-grid"
      data-company-name-lines={nameLines}
      data-node-id={nodeId}
      aria-busy={isLoading}
    >
      {isLoading
        ? Array.from({ length: placeholderCount }, (_, index) => (
          <ComparisonCardSkeleton key={`comparison-card-skeleton-${index}`} />
        ))
        : cards.map((card) => <ComparisonCard key={card.key} data={card} />)}
    </div>
  );
};

export default ComparisonCardGrid;
